import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, WHITELABEL_ID } from '../constants';
import withAuth from '../auth';
import { AuthContext } from './AuthContext';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';

const ManageBilling = () => {
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [plans, setPlans] = useState([]);
    const [openingPortal, setOpeningPortal] = useState(false);
    const [checkoutPlanId, setCheckoutPlanId] = useState(null);

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const { token, setToken } = useContext(AuthContext);
    const navigate = useNavigate();

    const authHeaders = {
        Authorization: `Bearer ${token}`,
    };

    // Load current subscription + available plans
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setErrorMessage('');
            try {
                const [subRes, plansRes] = await Promise.all([
                    axios.get(
                        `${API_URL}/api/wl/${WHITELABEL_ID}/manager/subscription`,
                        { headers: authHeaders }
                    ),
                    axios.get(
                        `${API_URL}/api/wl/manager/plans`,
                        { headers: authHeaders }
                    ),
                ]);

                setSubscription(subRes.data);
                setPlans(plansRes.data || []);
            } catch (error) {
                console.error('Error fetching billing data:', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('Unable to load billing info. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, navigate, setToken]);

    const formatDateTime = (value) => {
        if (!value) return '-';
        try {
            return moment(value).format('YYYY-MM-DD HH:mm');
        } catch (e) {
            return value;
        }
    };

    const formatCurrency = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'CAD',
        trailingZeroDisplay: 'stripIfInteger'
    });

    const formatPrice = (plan) => {
        if (!plan || !plan.amount) return '';
        const amount = parseFloat(plan.amount);
        return `${formatCurrency.format(amount)} / ${plan.interval}`;
    };

    const isActiveSub = subscription && subscription.status === 'active';

    const handleOpenPortal = () => {
        setErrorMessage('');
        setSuccessMessage('');
        setOpeningPortal(true);

        const return_url = `${window.location.origin}/manage-billing`;

        axios.post(
            `${API_URL}/api/wl/${WHITELABEL_ID}/manager/stripe/portal`,
            { return_url },
            {
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(response => {
                const url = response.data?.url;
                if (!url) {
                    setErrorMessage('Unable to open billing portal: missing URL.');
                    setOpeningPortal(false);
                    return;
                }
                // Stripe Billing Portal is *always* hosted by Stripe
                window.location.href = url;
            })
            .catch(error => {
                console.error('Error opening billing portal:', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                } else if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors)
                        .map(key => errors[key].join(' '))
                        .join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred while opening the billing portal. Please try again later.');
                }
                setOpeningPortal(false);
            });
    };

    const handleChoosePlan = (plan) => {
        if (!plan) return;

        setErrorMessage('');
        setSuccessMessage('');
        setCheckoutPlanId(plan.id);

        const success_url = `${window.location.origin}/manage-billing`;
        const cancel_url = `${window.location.origin}/manage-billing`;

        axios.post(
            `${API_URL}/api/wl/${WHITELABEL_ID}/manager/billing/checkout`,
            {
                plan_id: plan.id,
                success_url,
                cancel_url,
            },
            {
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(response => {
                const url = response.data?.url;
                if (!url) {
                    setErrorMessage('Unable to open checkout: missing URL.');
                    setCheckoutPlanId(null);
                    return;
                }
                // Stripe Checkout is hosted by Stripe; we redirect there
                window.location.href = url;
            })
            .catch(error => {
                console.error('Error creating checkout session:', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                } else if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors)
                        .map(key => errors[key].join(' '))
                        .join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred while starting checkout. Please try again later.');
                }
                setCheckoutPlanId(null);
            });
    };

    return (
        <div className="container mt-5">
            <h1 className="text-center mb-4">Billing & Subscription</h1>

            {successMessage && <div className="alert alert-success">{successMessage}</div>}
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

            {loading ? (
                <div className="d-flex justify-content-center align-items-center my-4">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-md-8 offset-md-2">
                        {/* Current subscription card */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">Current Subscription</h5>
                                {subscription && subscription.status !== 'none' ? (
                                    <>
                                        <p className="mb-1">
                                            Status: <b>{subscription.status || 'none'}</b>
                                        </p>
                                        {subscription.plan_name && (
                                            <p className="mb-1">
                                                Plan: <b>{subscription.plan_name}</b>
                                            </p>
                                        )}
                                        <p className="mb-1">
                                            Current period start:{' '}
                                            <b>{formatDateTime(subscription.current_period_start)}</b>
                                        </p>
                                        <p className="mb-3">
                                            Current period end:{' '}
                                            <b>{formatDateTime(subscription.current_period_end)}</b>
                                        </p>
                                    </>
                                ) : (
                                    <p>No active subscription for this project yet.</p>
                                )}

                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleOpenPortal}
                                    disabled={openingPortal}
                                >
                                    {openingPortal ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            &nbsp;Opening billing portal...
                                        </>
                                    ) : (
                                        'Manage payment methods & invoices'
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Plans / change plan section */}
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Choose / Change Plan</h5>
                                {plans.length === 0 ? (
                                    <p>No plans configured. Ask your favourite developer, which is apparently you.</p>
                                ) : (
                                    <div className="row">
                                        {plans.map((plan) => {
                                            const isCurrent =
                                                isActiveSub &&
                                                subscription &&
                                                Number(subscription.plan_id) === Number(plan.id);

                                            return (
                                                <div className="col-md-6 mb-3" key={plan.id}>
                                                    <div className={`card h-100 ${isCurrent ? 'border-success' : ''}`}>
                                                        <div className="card-body d-flex flex-column">
                                                            <h6 className="card-title mb-1">{plan.name}</h6>
                                                            <p className="mb-2">
                                                                <strong>{formatPrice(plan)}</strong>
                                                            </p>
                                                            <div className="mt-auto">
                                                                {isCurrent ? (
                                                                    <span className="badge bg-success">
                                                                        Current plan
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={() => handleChoosePlan(plan)}
                                                                        disabled={checkoutPlanId === plan.id}
                                                                    >
                                                                        {checkoutPlanId === plan.id ? (
                                                                            <>
                                                                                <Spinner
                                                                                    as="span"
                                                                                    animation="border"
                                                                                    size="sm"
                                                                                    role="status"
                                                                                    aria-hidden="true"
                                                                                />
                                                                                &nbsp;Redirecting to checkout...
                                                                            </>
                                                                        ) : (
                                                                            (subscription && subscription.status !== 'none')
                                                                                ? 'Change to this plan'
                                                                                : 'Start subscription'
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default withAuth(ManageBilling);
